import { useContext, useEffect, useState } from "react";
import type { ElementType } from "react";
import { useNavigate, useParams } from "react-router-dom";
import NoDataFound from "@/components/NoDataFound";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { brandService } from "@/shared/services/brandService";
import { subscriptionService } from "@/features/subscriptions/services/subscriptionService";
import CurrentAvailableCampaignCard from "../components/CurrentAvailableCampaignCard";
import SubscriptionPlan from "../components/SubscriptionPlan";
import WhatYouGetBySubscribe from "../components/WhatYouGetBySubscribe";
import { logger } from "@/helpers/loggerHelper";
import { HTTP_STATUS } from "@/config/constants";

type UserRecord = Record<string, any>;
type BrandData = Record<string, any> & {
  brand_name?: string;
  id: string | number;
};
type PackageData = Record<string, any> & {
  id: string | number;
};
type GlobalContextValue = {
  loggedInUser?: UserRecord;
};
type Loader = (_show: boolean) => void;

const SubscriptionPlanComponent = SubscriptionPlan as ElementType;

const SelfSubscription = () => {
  const { loggedInUser } = useContext(GlobalContext) as GlobalContextValue;
  const navigate = useNavigate();
  const { slug } = useParams();

  const loader = useContext(LoaderContext) as Loader;
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState<PackageData[]>([]);
  const [brandData, setBrandData] = useState<BrandData | null>(null);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const brandRes = await brandService.getBrandBySlug(slug);
      if (brandRes.status === HTTP_STATUS.OK) {
        const brand = brandRes.data;
        setBrandData(brand);
        const res = await subscriptionService.getPackages(brand.id);
        if (res.status === HTTP_STATUS.OK) {
          const data = res?.data?.packages;
          if (data && data.length) {
            setPackages(res.data.packages);
          }
        }
      }
    } catch (err) {
      logger.error("Error in fetchPackages", { err });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    // Preserve the existing mount-only package fetch behavior during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loader(loading);
    // Preserve the existing loader update behavior during migration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (loading) {
    return <NoDataFound message="Loading" />;
  } else if (!loading && !brandData) {
    return <NoDataFound message="Coming Soon.." height="50vh" />;
  }

  const renderSubscriptionPackages = () => {
    if (!loading && packages.length === 0) {
      return <NoDataFound message="No Plan found for this brand" />;
    }
    return (
      <>
        <h2 className="secondary-heading">
          Choose the plan that works for you
        </h2>
        <div className="plans">
          <div className="row justify-content-center align-items-stretch">
            {packages.map((p: PackageData) => (
              <div className="col-md-5 my-3" key={p.id}>
                <SubscriptionPlanComponent
                  planData={p}
                  brandData={brandData}
                  navigate={navigate}
                  loggedInUser={loggedInUser}
                />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="self-subscription-page">
      <section className="top-section">
        <div className="container">
          <h1 className="main-heading">{`Welcome, ${brandData.brand_name}!`}</h1>
          {renderSubscriptionPackages()}
        </div>
      </section>
      <section className="bottom-section">
        <WhatYouGetBySubscribe />
        <div className="container contact-us my-5 py-5 text-center">
          <h2 className="main-heading text-center">Questions? Need Help?</h2>
          <p className="contact-email my-4">
            Email us at{" "}
            <a href="mailto:JINYARamenBar@hyperlocology.com">
              JINYARamenBar@hyperlocology.com
            </a>
          </p>
        </div>
        <div className="container current-campaigns-available">
          <h2 className="main-heading text-left">
            Current campaigns available for you with subscription:
          </h2>
          <div className="row my-5">
            <div className="col-md-6 my-3">
              <CurrentAvailableCampaignCard
                campaignName="Lunch Campaign"
                img={`/img/subscription/lunch-campaign.png`}
                date="June 15th, 2021 - July 31st, 2021"
              />
            </div>
            <div className="col-md-6 my-3">
              <CurrentAvailableCampaignCard
                campaignName="Dinner Campaign"
                img={`/img/subscription/dinner-campaign.png`}
                date="June 15th, 2021 - July 31st, 2021"
              />
            </div>
            <div className="col-md-6 my-3">
              <CurrentAvailableCampaignCard
                campaignName="Hiring Campaign"
                img={`/img/subscription/hiring-campaign.png`}
                date="June 15th, 2021 - July 31st, 2021"
              />
            </div>
            <div className="col-md-6 my-3">
              <CurrentAvailableCampaignCard
                campaignName="Beer, Cocktails and Spirits Campaign"
                img={`/img/subscription/beer-cocktail-campaign.png`}
                date="June 15th, 2021 - July 31st, 2021"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SelfSubscription;
